import { useSEO } from "@/hooks/useSEO";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, errMsg, fmtDate, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, Circle, Zap, FileText, CalendarPlus, Camera, KeyRound, Loader2, QrCode, IdCard, Plane, Upload, Clock, AlertTriangle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import AuthShell from "@/components/AuthShell";

const DAMAGE_AREAS = [
  { key: "front", label: "Front" },
  { key: "rear", label: "Rear" },
  { key: "left", label: "Driver side" },
  { key: "right", label: "Passenger side" },
];

export default function CheckIn() {
  useSEO({ title: "Express Check-In — Nassau Airport Car Rental | Easy Drive", description: "Check in online before you land: upload your license and details so your Easy Drive rental is ready the moment you arrive in Nassau.", path: "/check-in" });
  const [ref, setRef] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [damageNotes, setDamageNotes] = useState("");
  const [damageUploads, setDamageUploads] = useState({});
  const [dropPhotos, setDropPhotos] = useState({ vehicle: null, key: null, fuel: null });
  const [dropGps, setDropGps] = useState(null);
  const [dropSubmitting, setDropSubmitting] = useState(false);
  const [dropDone, setDropDone] = useState(false);
  const [idNumber, setIdNumber] = useState("");
  const [flightNumber, setFlightNumber] = useState("");
  const [savingDetails, setSavingDetails] = useState(false);
  const [licenseUploading, setLicenseUploading] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());

  const captureLocation = () => {
    if (!navigator.geolocation) return toast.error("Location is not available on this device");
    navigator.geolocation.getCurrentPosition(
      (pos) => setDropGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => toast.error("Location permission denied — enable it to drop off remotely"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const submitDropoff = async () => {
    if (!dropPhotos.vehicle || !dropPhotos.key || !dropPhotos.fuel)
      return toast.error("Add all three photos: vehicle condition, key placement, fuel gauge");
    if (!dropGps) return toast.error("Share the vehicle's location to drop off remotely");
    setDropSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("vehicle_photo", dropPhotos.vehicle);
      fd.append("key_photo", dropPhotos.key);
      fd.append("fuel_photo", dropPhotos.fuel);
      fd.append("email", email.trim().toLowerCase());
      fd.append("gps_lat", String(dropGps.lat));
      fd.append("gps_lng", String(dropGps.lng));
      if (dropGps.accuracy != null) fd.append("gps_accuracy", String(dropGps.accuracy));
      await api.post(`/bookings/${booking.id}/return-photos`, fd);
      setDropDone(true);
      setBooking({ ...booking, return_submitted: true });
      toast.success("Drop-off submitted — your deposit releases once the team verifies the photos");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setDropSubmitting(false);
    }
  };
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get("mode") === "checkout" ? "return" : "checkin");

  useEffect(() => {
    const r = searchParams.get("ref");
    const e = searchParams.get("email");
    if (r && e) {
      setRef(r);
      setEmail(e);
      const n = searchParams.get("name") || "";
      if (n) setName(n);
      api.get("/bookings/lookup", { params: { ref: r, email: e, name: n || undefined } })
        .then(({ data }) => {
          setBooking(data);
          setDamageNotes(data.damage_notes || "");
          localStorage.setItem(`booking_email_${data.id}`, e.trim().toLowerCase());
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!booking || booking.status !== "confirmed" || booking.vehicle_status === "ready" || booking.picked_up_at) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/bookings/lookup", { params: { ref: booking.booking_ref, email: (email || "").trim().toLowerCase() } });
        setBooking((prev) => (prev ? { ...prev, ...data } : prev));
      } catch (e2) {}
    }, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?.id, booking?.status, booking?.vehicle_status]);

  useEffect(() => {
    if (booking?.picked_up_at && booking?.status === "confirmed" && searchParams.get("mode") !== "checkout" && mode === "checkin") {
      setMode("return");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?.picked_up_at, booking?.status]);

  const lookup = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.get("/bookings/lookup", { params: { ref, email, name } });
      setBooking(data);
      setDamageNotes(data.damage_notes || "");
      localStorage.setItem(`booking_email_${data.id}`, email.trim().toLowerCase());
    } catch (e2) {
      setError(errMsg(e2));
    } finally {
      setLoading(false);
    }
  };

  const payNow = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/pay/stripe`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e2) {
      toast.error(errMsg(e2));
      setPaying(false);
    }
  };

  const uploadDamage = async (key, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", `damage_${key}`);
      fd.append("email", email.trim().toLowerCase());
      await api.post(`/bookings/${booking.id}/license`, fd);
      setDamageUploads((m) => ({ ...m, [key]: true }));
      toast.success(`${DAMAGE_AREAS.find((a) => a.key === key).label} photo uploaded`);
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };

  const saveDamageNotes = async () => {
    try {
      await api.post(`/bookings/${booking.id}/damage-notes`, { notes: damageNotes.trim(), email: email.trim().toLowerCase() });
      setBooking({ ...booking, damage_reported_at: new Date().toISOString() });
      toast.success("Damage inspection saved — this protects your deposit");
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };

  useEffect(() => {
    if (!booking) return;
    setIdNumber((booking.renter_type || "local") === "local" ? booking.nib_number || "" : booking.passport_number || "");
    setFlightNumber(booking.flight_number || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?.id]);

  useEffect(() => {
    if (!booking) return;
    const t = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?.id]);

  const isLocal = booking ? (booking.renter_type || "local") === "local" : true;
  const needsFlight = booking ? (booking.dropoff_location || "").toLowerCase().includes("airport") : false;

  const uploadLicense = async (file) => {
    if (!file) return;
    setLicenseUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", "license_front");
      fd.append("email", email.trim().toLowerCase());
      await api.post(`/bookings/${booking.id}/license`, fd);
      setBooking({ ...booking, license_uploaded: true });
      toast.success("License uploaded — one step closer to the keys");
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setLicenseUploading(false);
    }
  };

  const saveDetails = async () => {
    setSavingDetails(true);
    try {
      const payload = { email: email.trim().toLowerCase() };
      if (isLocal) payload.nib_number = idNumber; else payload.passport_number = idNumber;
      if (needsFlight) payload.flight_number = flightNumber;
      await api.post(`/bookings/${booking.id}/details`, payload);
      setBooking({
        ...booking,
        ...(isLocal ? { nib_number: idNumber.trim() } : { passport_number: idNumber.trim() }),
        ...(needsFlight ? { flight_number: flightNumber.trim() } : {}),
      });
      toast.success("Details saved to your booking");
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setSavingDetails(false);
    }
  };

  if (!booking) {
    return (
      <AuthShell
        testid="checkin-page"
        logoTestid="checkin-logo"
        eyebrow={<><Zap size={16} className="gold-text" /> Express Check-In & Rental Return</>}
        title={<>Skip the <span className="italic gold-text">counter</span></>}
        sub="Scanned your QR code? Find your booking below — then choose Check In to get road-ready, or Return Rental to drop off in minutes."
      >
        <form onSubmit={lookup} className="mt-10 space-y-7">
          <div>
            <Label className="font-ui text-sm tracking-wide text-muted-foreground">Booking reference (e.g. ED-1A2B3C)</Label>
            <Input data-testid="checkin-ref-input" required value={ref} onChange={(e) => setRef(e.target.value)} className="input-dark mt-2 h-12 text-base rounded-2xl px-4" />
          </div>
          <div>
            <Label className="font-ui text-sm tracking-wide text-muted-foreground">Email used at booking</Label>
            <Input data-testid="checkin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-dark mt-2 h-12 text-base rounded-2xl px-4" />
          </div>
          <div>
            <Label className="font-ui text-sm tracking-wide text-muted-foreground">Full name on booking</Label>
            <Input data-testid="checkin-name-input" required value={name} onChange={(e) => setName(e.target.value)} className="input-dark mt-2 h-12 text-base rounded-2xl px-4" />
          </div>
          {error && <p data-testid="checkin-error" className="text-base text-destructive font-ui">{error}</p>}
          <Button data-testid="checkin-lookup-button" disabled={loading} className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui h-12 text-base tracking-wide transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(199,30,46,0.35)]">
            {loading ? "Finding booking…" : "Find My Booking"}
          </Button>
        </form>
      </AuthShell>
    );
  }

  const isConfirmed = booking.status === "confirmed";
  const showCheckin = mode === "checkin" || !isConfirmed;
  const showReturn = mode === "return" && isConfirmed;

  const steps = [
    { key: "payment", label: "Rental paid", done: booking.status === "confirmed" || booking.status === "completed" },
    { key: "docs", label: "Driver's license uploaded", done: !!booking.license_uploaded },
    { key: "contract", label: "Rental contract signed", done: !!booking.contract_signed_at },
    { key: "idnumber", label: isLocal ? "NIB number provided" : "Passport number provided", done: isLocal ? !!(booking.nib_number || "").trim() : !!(booking.passport_number || "").trim() },
    ...(needsFlight ? [{ key: "flight", label: "Flight number provided", done: !!(booking.flight_number || "").trim() }] : []),
  ];
  const allDone = steps.every((s) => s.done);
  const doneCount = steps.filter((s) => s.done).length;

  const returnDue = (() => {
    try {
      const raw = (booking.dropoff_time || "").trim();
      let hh = 17, mm = 0;
      const m24 = raw.match(/^(\d{1,2}):(\d{2})$/);
      const m12 = raw.match(/^(\d{1,2}):(\d{2})\s*([AaPp])\s*[Mm]?$/);
      if (m24) { hh = +m24[1]; mm = +m24[2]; }
      else if (m12) { hh = (+m12[1] % 12) + (/p/i.test(m12[3]) ? 12 : 0); mm = +m12[2]; }
      const d = new Date(`${booking.end_date}T${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00`);
      return isNaN(d.getTime()) ? null : d;
    } catch { return null; }
  })();
  const nasNow = new Date(new Date(nowTick).toLocaleString("en-US", { timeZone: "America/Nassau" }));
  const returnDayStart = booking.end_date ? new Date(`${booking.end_date}T00:00:00`) : null;
  const showReturnBanner = booking.status === "confirmed" && !booking.return_submitted && !!returnDue && !!returnDayStart && nasNow >= returnDayStart;
  const msLeft = showReturnBanner ? returnDue.getTime() - nasNow.getTime() : 0;
  const overHours = Math.max(0, Math.floor(-msLeft / 3600000));
  const fmtDur = (ms) => `${Math.floor(Math.abs(ms) / 3600000)}h ${String(Math.floor((Math.abs(ms) % 3600000) / 60000)).padStart(2, "0")}m`;

  return (
    <div data-testid="checkin-dashboard" className="max-w-2xl mx-auto px-6 pt-36 pb-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-card border border-border rounded-3xl p-8 sm:p-10 shadow-[0_24px_60px_rgba(26,10,12,0.08)]"
      >
        <p className="font-ui text-sm tracking-[0.25em] uppercase text-muted-foreground flex items-center gap-2">
          <Zap size={16} className="gold-text" /> Express Check-In & Rental Return
        </p>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight">{booking.vehicle_name}</h1>
        <p className="mt-3 text-base text-muted-foreground font-ui">
          {booking.booking_ref} · {fmtDate(booking.start_date)} → {fmtDate(booking.end_date)} · {fmtMoney(booking.amount_due || booking.total)}
        </p>
        {booking.docs_approved && (
          <p data-testid="verified-renter-badge" className="mt-3 inline-flex items-center gap-1.5 text-xs font-ui font-semibold tracking-wide uppercase text-emerald-700 border border-emerald-300 bg-emerald-50 rounded-full px-3.5 py-1.5">
            <CheckCircle2 size={13} /> Verified Renter — documents approved
          </p>
        )}
        <p className="mt-1.5 text-sm text-muted-foreground font-ui">Pickup: {booking.pickup_location}</p>

        {showReturnBanner && (
          <div data-testid="return-countdown-banner" className={`mt-5 rounded-2xl border p-4 ${msLeft > 0 ? "border-amber-300 bg-amber-50" : "border-red-300 bg-red-50"}`}>
            <p className={`font-ui font-semibold text-sm flex items-center gap-2 ${msLeft > 0 ? "text-amber-800" : "text-red-700"}`}>
              {msLeft > 0 ? <Clock size={15} /> : <AlertTriangle size={15} />}
              {msLeft > 0
                ? `Return by ${booking.dropoff_time || "17:00"} today — ${fmtDur(msLeft)} left`
                : `Return overdue by ${fmtDur(msLeft)} — late fee accruing: $${overHours * 25}`}
            </p>
            <p className={`mt-1 text-xs font-ui leading-relaxed ${msLeft > 0 ? "text-amber-700" : "text-red-600"}`}>
              {msLeft > 0
                ? "Late returns are billed at $25/hour. Running behind? Extend instantly to avoid fees."
                : `$25/hour is being added (${overHours}h so far). Submit your return photos or extend now to stop the clock.`}
            </p>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
              <a data-testid="return-banner-extend-link" href={`/extend/${booking.id}`} className="text-xs font-ui font-semibold underline underline-offset-2 text-[#1A1A1A]">Extend rental</a>
              <a data-testid="return-banner-photos-link" href={`/rental-return?ref=${booking.booking_ref}&email=${encodeURIComponent(email)}`} className="text-xs font-ui font-semibold underline underline-offset-2 text-[#1A1A1A]">Submit return photos</a>
            </div>
          </div>
        )}

        {isConfirmed && (
          <div data-testid="checkin-mode-toggle" className="mt-6 grid grid-cols-2 gap-2 rounded-full border border-border bg-black/[0.03] p-1.5">
            <button
              type="button"
              data-testid="checkin-mode-checkin"
              onClick={() => setMode("checkin")}
              className={`rounded-full py-2.5 text-sm font-ui transition-all duration-200 ${showCheckin ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}
            >
              Check In
            </button>
            <button
              type="button"
              data-testid="checkin-mode-return"
              onClick={() => setMode("return")}
              className={`rounded-full py-2.5 text-sm font-ui transition-all duration-200 ${showReturn ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}
            >
              Return Rental
            </button>
          </div>
        )}

        {showCheckin && isConfirmed && (
          <div data-testid="checkin-vehicle-status" className="mt-6">
            <AnimatePresence mode="wait">
              {booking.picked_up_at ? (
                <motion.div
                  key="pickedup"
                  initial={{ opacity: 0, scale: 0.9, rotateX: -40 }}
                  animate={{ opacity: 1, scale: 1, rotateX: 0 }}
                  transition={{ duration: 0.5, type: "spring", bounce: 0.35 }}
                  data-testid="vehicle-status-picked-up"
                  className="rounded-2xl border border-emerald-400/40 bg-emerald-400/10 px-5 py-4 flex items-center gap-3.5 shadow-[0_8px_30px_rgba(16,185,129,0.15)]"
                >
                  <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-500/15">
                    <KeyRound size={20} className="text-emerald-600 relative" />
                  </span>
                  <div>
                    <p className="font-ui font-semibold text-base text-emerald-700">Picked up — you're all set!</p>
                    <p className="text-xs text-muted-foreground">Your pickup was processed on our side. Enjoy the drive — return details are below when you need them.</p>
                  </div>
                </motion.div>
              ) : booking.vehicle_status === "ready" ? (
                <motion.div
                  key="ready"
                  initial={{ opacity: 0, scale: 0.9, rotateX: -40 }}
                  animate={{ opacity: 1, scale: 1, rotateX: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.5, type: "spring", bounce: 0.35 }}
                  data-testid="vehicle-status-ready"
                  className="rounded-2xl border border-emerald-400/40 bg-emerald-400/10 px-5 py-4 flex items-center gap-3.5 shadow-[0_8px_30px_rgba(16,185,129,0.15)]"
                >
                  <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-500/15">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400/30 animate-ping" />
                    <KeyRound size={20} className="text-emerald-600 relative" />
                  </span>
                  <div>
                    <p className="font-ui font-semibold text-base text-emerald-700">Your vehicle is ready!</p>
                    <p className="text-xs text-muted-foreground">Keys are at handover — show this screen and drive.</p>
                  </div>
                </motion.div>
              ) : booking.rental_started_at || booking.vehicle_status === "preparing" ? (
                <motion.div
                  key="preparing"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4 }}
                  data-testid="vehicle-status-preparing"
                  className="rounded-2xl border border-amber-400/40 bg-amber-400/10 px-5 py-4 flex items-center gap-3.5"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-500/15">
                    <Loader2 size={20} className="text-amber-600 animate-spin" />
                  </span>
                  <div>
                    <p className="font-ui font-semibold text-base text-amber-700">Your vehicle is being prepared…</p>
                    <p className="text-xs text-muted-foreground">Final checks, cleaning &amp; fuel — this page flips live the moment it's ready.</p>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="waiting"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  data-testid="vehicle-status-waiting"
                  className="rounded-2xl border border-border bg-black/[0.02] px-5 py-4 flex items-center gap-3.5"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-black/5">
                    <QrCode size={20} className="text-muted-foreground" />
                  </span>
                  <div>
                    <p className="font-ui font-semibold text-base">At handover, show your pickup QR</p>
                    <p className="text-xs text-muted-foreground">When our team scans it, your live vehicle status appears right here.</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {showCheckin && (
          <>
            <div className="mt-8">
              <div className="flex items-center justify-between text-sm font-ui text-muted-foreground">
                <span>{doneCount} of {steps.length} complete</span>
                <span>{Math.round((doneCount / steps.length) * 100)}%</span>
              </div>
              <div className="mt-2 h-2 rounded-full bg-black/5 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(doneCount / steps.length) * 100}%` }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className={`h-full rounded-full ${allDone ? "bg-emerald-500" : "bg-primary"}`}
                />
              </div>
            </div>

            <div className="mt-7 space-y-3">
              {steps.map((s, i) => (
                <motion.div
                  key={s.key}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.15 + i * 0.12 }}
                  data-testid={`checkin-step-${s.key}`}
                  className={`flex items-center gap-3.5 rounded-2xl border px-5 py-4 ${s.done ? "border-emerald-400/30 bg-emerald-400/5" : "border-border bg-black/[0.02]"}`}
                >
                  {s.done ? <CheckCircle2 size={24} className="text-emerald-500 shrink-0" /> : <Circle size={24} className="text-muted-foreground shrink-0" />}
                  <span className={`text-base font-ui ${s.done ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</span>
                </motion.div>
              ))}
            </div>

            <div data-testid="guest-documents" className="mt-8 border border-border rounded-2xl p-5">
              <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2">
                <IdCard size={14} className="gold-text" /> Documents & details
              </p>
              <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                Everything we need before handover — add it here in under a minute and skip the counter.
              </p>

              <div className="mt-4 rounded-xl border border-border bg-black/[0.02] px-4 py-3.5">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-ui font-medium">Driver's license photo</p>
                  {booking.license_uploaded && (
                    <span data-testid="license-on-file" className="text-xs font-ui text-emerald-600 flex items-center gap-1 shrink-0"><CheckCircle2 size={13} /> On file</span>
                  )}
                </div>
                {!booking.license_uploaded && (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <label data-testid="license-camera-button" className={`flex items-center justify-center gap-2 rounded-full border border-primary/40 text-primary font-ui text-xs h-10 cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors ${licenseUploading ? "opacity-50 pointer-events-none" : ""}`}>
                      <Camera size={14} /> Take a photo
                      <input data-testid="license-camera-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadLicense(e.target.files?.[0] || null)} />
                    </label>
                    <label data-testid="license-upload-button" className={`flex items-center justify-center gap-2 rounded-full border border-border font-ui text-xs h-10 cursor-pointer hover:bg-black/5 transition-colors ${licenseUploading ? "opacity-50 pointer-events-none" : ""}`}>
                      <Upload size={14} /> {licenseUploading ? "Uploading…" : "Upload file"}
                      <input data-testid="license-file-input" type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={(e) => uploadLicense(e.target.files?.[0] || null)} />
                    </label>
                  </div>
                )}
              </div>

              <div className="mt-3 rounded-xl border border-border bg-black/[0.02] px-4 py-3.5">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-ui font-medium">{isLocal ? "NIB number" : "Passport number"}</p>
                  {((isLocal ? booking.nib_number : booking.passport_number) || "").trim() && (
                    <span data-testid="idnumber-on-file" className="text-xs font-ui text-emerald-600 flex items-center gap-1 shrink-0"><CheckCircle2 size={13} /> Saved</span>
                  )}
                </div>
                <Input
                  data-testid="guest-idnumber-input"
                  value={idNumber}
                  onChange={(e) => setIdNumber(e.target.value)}
                  placeholder={isLocal ? "Your National Insurance Board number" : "Your passport number"}
                  className="input-dark mt-2 h-11 text-sm rounded-xl px-4"
                />
              </div>

              {needsFlight && (
                <div className="mt-3 rounded-xl border border-border bg-black/[0.02] px-4 py-3.5">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-ui font-medium flex items-center gap-1.5"><Plane size={14} className="gold-text" /> Flight number & airline</p>
                    {(booking.flight_number || "").trim() && (
                      <span data-testid="flight-on-file" className="text-xs font-ui text-emerald-600 flex items-center gap-1 shrink-0"><CheckCircle2 size={13} /> Saved</span>
                    )}
                  </div>
                  <Input
                    data-testid="guest-flight-input"
                    value={flightNumber}
                    onChange={(e) => setFlightNumber(e.target.value)}
                    placeholder="e.g. AA 1234 — American Airlines"
                    className="input-dark mt-2 h-11 text-sm rounded-xl px-4"
                  />
                  {((booking.flight_number || "").trim() || flightNumber.trim()) && (
                    <p data-testid="flight-tracking-note" className="mt-2 text-xs text-sky-700 font-ui flex items-center gap-1.5"><Plane size={12} /> We track your flight — if it's delayed, your pickup shifts automatically.</p>
                  )}
                </div>
              )}

              <Button data-testid="guest-details-save-button" onClick={saveDetails} disabled={savingDetails} variant="outline" className="mt-3 w-full rounded-full border-primary/40 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground font-ui">
                {savingDetails ? "Saving…" : "Save Details"}
              </Button>
            </div>

            <div data-testid="damage-inspection" className="mt-8 border border-border rounded-2xl p-5">
              <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2">
                <Camera size={14} className="gold-text" /> Vehicle Damage Inspection
              </p>
              <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                Photograph all four sides at pickup and note any existing scratches or dents — this protects your $150 deposit.
              </p>
              {booking.damage_reported_at && (
                <p data-testid="damage-submitted" className="mt-2 text-xs font-ui text-emerald-600 flex items-center gap-1.5"><CheckCircle2 size={12} /> Inspection notes on file</p>
              )}
              <div className="mt-4 grid grid-cols-2 gap-3">
                {DAMAGE_AREAS.map((a) => (
                  <div key={a.key}>
                    <Input data-testid={`damage-photo-${a.key}`} type="file" accept="image/*" capture="environment" onChange={(e) => uploadDamage(a.key, e.target.files?.[0] || null)} className="input-dark text-xs file:text-primary file:mr-2" />
                    <p className="mt-1 text-[10px] font-ui text-muted-foreground">
                      {a.label} photo {damageUploads[a.key] && <span data-testid={`damage-photo-done-${a.key}`} className="text-emerald-600 font-semibold">uploaded ✓</span>}
                    </p>
                  </div>
                ))}
              </div>
              <textarea
                data-testid="damage-notes-input"
                value={damageNotes}
                onChange={(e) => setDamageNotes(e.target.value)}
                placeholder="Note any existing scratches, dents or marks (e.g. small scratch on rear bumper, scuffed hubcap)…"
                className="mt-3 w-full input-dark rounded-xl px-4 py-3 text-sm min-h-[80px]"
              />
              <Button data-testid="damage-save-button" onClick={saveDamageNotes} variant="outline" className="mt-3 w-full rounded-full border-primary/40 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground font-ui">
                Save Inspection Notes
              </Button>
            </div>

            {allDone ? (
              <div data-testid="checkin-complete" className="mt-8 border border-emerald-400/40 bg-emerald-400/5 rounded-2xl p-6">
                <p className="font-ui font-semibold text-base text-emerald-600">Express pickup approved</p>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                  Everything is complete — show this screen at handover and you'll be on the
                  road in under two minutes.
                </p>
              </div>
            ) : (
              <div className="mt-8 space-y-3">
                {booking.status === "pending_payment" && (
                  <Button data-testid="checkin-pay-button" disabled={paying} onClick={payNow} className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui h-12 text-base tracking-wide transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(199,30,46,0.35)]">
                    {paying ? "Preparing checkout…" : "Pay Now — Card, Google Pay or Apple Pay"}
                  </Button>
                )}
                {!booking.contract_signed_at && (
                  <Link to={`/contract/${booking.id}`} className="block">
                    <Button data-testid="checkin-contract-button" variant="outline" className="w-full rounded-full border-primary/40 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground font-ui h-12 text-base">
                      <FileText size={16} className="mr-2" /> Fill Out & Sign Contract
                    </Button>
                  </Link>
                )}
              </div>
            )}

            <Link to={`/extend/${booking.id}`} className="block mt-4">
              <Button data-testid="checkin-extend-button" variant="outline" className="w-full rounded-full border-border bg-transparent hover:bg-black/5 hover:text-primary font-ui h-12 text-base">
                <CalendarPlus size={16} className="mr-2" /> Extend This Rental
              </Button>
            </Link>
          </>
        )}

        {showReturn && (
          <div data-testid="remote-dropoff" className="mt-8 border border-primary/30 rounded-2xl p-5">
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2">
              <Camera size={14} className="gold-text" /> Rental return — remote drop-off
            </p>
            <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
              Leaving the car without meeting our team? Take three fresh photos — vehicle condition, key placement, fuel gauge — and share the vehicle's location. Photos must be new: images from your pickup inspection won't be accepted.
            </p>
            {booking.return_submitted || dropDone ? (
              <p data-testid="dropoff-submitted" className="mt-3 text-xs font-ui text-emerald-600 flex items-center gap-1.5"><CheckCircle2 size={12} /> Drop-off photos submitted — deposit releases after verification</p>
            ) : (
              <div className="mt-4 space-y-3">
                {[{ k: "vehicle", l: "Vehicle condition photo" }, { k: "key", l: "Key placement photo" }, { k: "fuel", l: "Fuel gauge photo" }].map((p) => (
                  <div key={p.k}>
                    <Input data-testid={`dropoff-photo-${p.k}`} type="file" accept="image/*" capture="environment" onChange={(e) => setDropPhotos({ ...dropPhotos, [p.k]: e.target.files?.[0] || null })} className="input-dark text-xs file:text-primary file:mr-2" />
                    <p className="mt-1 text-[10px] font-ui text-muted-foreground">
                      {p.l} {dropPhotos[p.k] && <span data-testid={`dropoff-photo-done-${p.k}`} className="text-emerald-600 font-semibold">ready ✓</span>}
                    </p>
                  </div>
                ))}
                <Button data-testid="dropoff-location-button" type="button" variant="outline" onClick={captureLocation} className="w-full rounded-full border-border bg-transparent hover:bg-black/5 font-ui">
                  {dropGps ? `Location captured ✓ (${dropGps.lat.toFixed(4)}, ${dropGps.lng.toFixed(4)})` : "Share Vehicle Location"}
                </Button>
                <Button data-testid="dropoff-submit-button" onClick={submitDropoff} disabled={dropSubmitting} className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui h-12">
                  {dropSubmitting ? "Submitting…" : "Submit Rental Return"}
                </Button>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
}
